/**
 * 苹果端（iOS/Safari）隐藏态专用：WebAudio 调度分支（方案 B 的独立文件）
 *
 * 目标：
 * - 在 document.hidden === true 且 iOS/Safari 时，使用 WebAudio 无缝调度后续音频，避免 HTML5 play() 触发 NotAllowed
 * - 回到前台时自动卸载 WebAudio 分支，将控制权交还给 Howler（现有前台路径），避免双声与冲突
 * - 与现有队列/进度/完成回调保持一致的房间状态推进
 *
 * 使用方式（最少改动）：
 * - 在进入房间时调用 ensureAppleHiddenWebAudio(roomId)（可选，首次隐藏时自动创建也可）
 * - 在 WebSocket 收到新音频消息时，调用 feedAppleHiddenWebAudio(roomId, { id, text, audioUrl })
 * - 模块内部自动监听 visibilitychange：
 *   - hidden → 启用 WebAudio keepAlive 并继续调度
 *   - visible → 停止并销毁 WebAudio 分支，触发一次前台恢复（让 Howler 继续）
 */

import type { RootState } from "../state";
import { SeamlessWebAudioPlayer, type WebAudioItem } from "./webAudioPlayer";
import { getConfig } from "@/config";
import { emitter } from "@/utils/emitter/mitt";

/** 简易日志工具（统一前缀，便于检索） */
function logInfo(msg: string, extra?: any) {
  try {
    console.log(`[apple-wa] ${msg}`, extra ?? "");
    emitter.emit("mobileAudioLog", {
      level: "info",
      source: "apple-wa",
      message: `${msg} ${extra ? JSON.stringify(extra) : ""}`,
      timestamp: Date.now()
    });
  } catch {}
}
function logWarn(msg: string, extra?: any) {
  try {
    console.warn(`[apple-wa] ${msg}`, extra ?? "");
    emitter.emit("mobileAudioLog", {
      level: "warn",
      source: "apple-wa",
      message: `${msg} ${extra ? JSON.stringify(extra) : ""}`,
      timestamp: Date.now()
    });
  } catch {}
}
function logError(msg: string, extra?: any) {
  try {
    console.error(`[apple-wa] ${msg}`, extra ?? "");
    emitter.emit("mobileAudioLog", {
      level: "error",
      source: "apple-wa",
      message: `${msg} ${extra ? JSON.stringify(extra) : ""}`,
      timestamp: Date.now()
    });
  } catch {}
}

type Store = RootState & { [key: string]: any };

interface RoomWebAudioState {
  player: SeamlessWebAudioPlayer;
  roomId: string;
  attached: boolean; // visibility 监听是否已附着
  deferredStartTimer?: number; // 延迟启动WA的定时器（等待Howler接近结束）
  awaitingHowlerBoundary?: boolean; // 是否在等待Howler到达边界
  // WebAudio 播放列表的 index=0 对应主队列的索引（隐藏态通常为 currentIndex+1）
  baseStoreIndex?: number;
  // 前台交接标志（类型补齐）
  foreHandoff?: boolean;
  // 绑定到 <audio> 媒体元素，便于 iOS 后台继续输出
  sinkEl?: HTMLAudioElement | null;
  // 参数模式：隐藏/可见（在下一句开始前应用）
  paramMode?: "hidden" | "visible";
  // 首次进入隐藏态是否已做过“降载预填”
  hiddenInitPrefilled?: boolean;
  // 隐藏态延迟预填的定时器
  _hiddenPrefillTimer?: number;
  // 切换瞬间冻结：在该时间戳之前，除“持续放声”以外尽量不做任何计算/调度
  switchFreezeUntilTs?: number;
  // 冻结窗口内一次性日志标记，避免刷屏
  _freezeProgressLogged?: boolean;
  // 是否首次切换（使用更强保护）
  firstSwitch?: boolean;
  // 预隐藏/预可见 监听引用（便于解绑）
  blurHandler?: (e: Event) => void;
  pagehideHandler?: (e: Event) => void;
  focusHandler?: (e: Event) => void;
  pageshowHandler?: (e: Event) => void;
  // 已加入到 WA 播放列表的去重集合（按 item.id）
  addedIds?: Set<string>;
  // 记录每个音频ID已投喂的最新版本号，避免重复排程
  idVersionMap?: Map<string, number>;
  // 可见侧兜底播放的轻防抖时间戳
  lastVisiblePlayTs?: number;
  // 待切换目标是否已完成至少一条音频的预解码
  switchTargetReady?: boolean;
  // 当前已到达边界但在等待目标预解码完成
  awaitingTargetDecode?: boolean;
  // 目标首条是否正在解码
  targetDecoding?: boolean;
  // 待缓冲的目标产品条目（为防止并发解码压制首条）
  pendingTargetFeed?: WebAudioItem[];
  // 准备阶段的目标产品（点击后设置，直到边界切换）
  preparingTargetPid?: string | null;
  // 目标是否已初步就绪（首条解码 OK）
  targetPrepared?: boolean;
  // 边界切换后首条暂缓去重的标记
  firstAfterSwitchPending?: boolean;
  firstAfterSwitchKey?: string | null;
  firstAfterSwitchAudioId?: string | null;
  firstAfterSwitchPid?: string | null;
  // 首条音频是否仍在等待完成回调
  firstAfterSwitchAwaitingComplete?: boolean;
}

/** iOS / iPadOS / Safari 判定 */
function isIOSLike(): boolean {
  const ua = navigator.userAgent || "";
  const iOS = /iPhone|iPad|iPod/i.test(ua);
  const iPadOS = /Macintosh/.test(ua) && (navigator as any).maxTouchPoints > 1;
  return iOS || iPadOS;
}
function isSafari(): boolean {
  const ua = navigator.userAgent || "";
  return /Safari/i.test(ua) && !/Chrome|Chromium|Edge/i.test(ua);
}

/** 从 store 的 roomState 获取/设置 WebAudio 管理器 */
function getRoomWAState(
  store: Store,
  roomId: string
): RoomWebAudioState | null {
  const state = store.roomStates.get(roomId);
  if (!state) return null;
  return (state as any).__appleWA__ || null;
}
function setRoomWAState(
  store: Store,
  roomId: string,
  wa: RoomWebAudioState | null
) {
  const state = store.roomStates.get(roomId);
  if (!state) return;
  (state as any).__appleWA__ = wa;
}

/** 确保为房间创建 WebAudio 播放器（仅 iOS/Safari 生效） */
export function ensureAppleHiddenWebAudio(this: Store, roomId: string) {
  if (!roomId || !this.roomStates.has(roomId)) return;
  if (!(isIOSLike() || isSafari())) return; // 非苹果端不创建

  const existed = getRoomWAState(this, roomId);
  if (existed && existed.player) return; // 已存在

  const player = new SeamlessWebAudioPlayer(roomId);

  // 🔧 修复：创建WebAudio播放器时立即应用房间隔离逻辑
  const isCurrentRoom = this.currentRoomId === roomId;
  if (isCurrentRoom) {
    try {
      player.setMuted && player.setMuted(false);
    } catch {}
    logInfo(`🔊 创建时设置当前房间${roomId}WebAudio有声播放`);
  } else {
    try {
      player.setMuted && player.setMuted(true);
    } catch {}
    logInfo(`🔇 创建时设置非当前房间${roomId}WebAudio静音播放`);
  }

  try {
    // A 方案：开启严格句间边界，确保“上一句可听部分结束+保护”后才启动下一句
    (player as any).setEnsurePrevComplete &&
      (player as any).setEnsurePrevComplete(true, 20);
    logInfo(`param: ensurePrevComplete enabled (guardMs=20) room=${roomId}`);
  } catch {}
  logInfo(`create WebAudio player for room=${roomId}`, {
    ua: navigator.userAgent,
    hidden: typeof document !== "undefined" ? document.hidden : false,
    iosLike: isIOSLike(),
    safari: isSafari()
  });

  // 绑定进度回调：更新当前队列项的进度与时间信息（与前台 Howler 保持一致）
  player.setProgressCallback((index, progress) => {
    try {
      const wa = getRoomWAState(this, roomId) as any;
      if (
        wa &&
        typeof wa.switchFreezeUntilTs === "number" &&
        Date.now() < wa.switchFreezeUntilTs
      ) {
        // 切换瞬间：跳过 UI 进度与重计算，保持仅音频输出
        try {
          if (!wa._freezeProgressLogged) {
            wa._freezeProgressLogged = true;
            logInfo(
              `freeze: progress gated room=${roomId} idx=${index} p=${(progress * 100).toFixed(1)}% untilTs=${wa.switchFreezeUntilTs}`
            );
          }
        } catch {}
        return;
      }
      try {
        if (wa && wa._freezeProgressLogged) {
          wa._freezeProgressLogged = false;
        }
      } catch {}
      const state = this.roomStates.get(roomId);
      if (!state) return;
      const status = player.getStatus();
      let queueIndex: number | null = null;
      if (
        status?.currentItem &&
        typeof (status.currentItem as any)?.storeIndex === "number" &&
        (status.currentItem as any).storeIndex! >= 0
      ) {
        queueIndex = Math.min(
          state.queue.length - 1,
          Math.max(0, (status.currentItem as any).storeIndex!)
        );
      }
      if (
        (queueIndex === null ||
          queueIndex < 0 ||
          queueIndex >= state.queue.length) &&
        status?.currentItem?.id
      ) {
        try {
          const idx = state.queue.findIndex(
            (x: any) => x && x.id === status.currentItem!.id
          );
          if (idx >= 0) queueIndex = idx;
        } catch {}
      }
      if (
        queueIndex === null ||
        queueIndex < 0 ||
        queueIndex >= state.queue.length
      ) {
        queueIndex = state.currentIndex;
      }
      if (queueIndex < 0 || queueIndex >= state.queue.length) return;
      if (queueIndex !== state.currentIndex) {
        state.currentIndex = queueIndex;
      }
      const current = state.queue[queueIndex];
      if (!current) return;
      // 同步时间型进度（用于前台恢复精准续播）
      const dur = status?.duration || 0;
      const curTime = status?.currentTime || 0;
      (current as any).audioTimeProgress = progress;
      (current as any).audioCurrentTime = curTime;
      (current as any).audioDuration = dur;
      const totalChars = current.text?.length || 0;
      current.playedCharCount = Math.floor(progress * totalChars);
      current.status = "playing";
      // 🔧 强制触发Vue响应式更新
      const q = [...state.queue];
      q[state.currentIndex] = { ...current } as any;
      state.queue = q as any;
      const updatedCurrent = state.queue[state.currentIndex] || null;
      if (Math.floor(progress * 10) % 2 === 0) {
        // 注释频繁的进度日志，减少日志输出
        // logInfo(`progress room=${roomId} idx=${state.currentIndex}/${state.queue.length} ${(progress*100).toFixed(1)}%`);
      }

      if (
        (state as any).pendingSwitchSuccessMessage &&
        (curTime >= 0.4 || progress >= 0.1)
      ) {
        try {
          emitter.emit("productAudioStarted", {
            roomId,
            productId: (updatedCurrent as any)?.productId || null,
            audioId: (updatedCurrent as any)?.id || null,
            source: "apple-wa"
          });
        } catch {}
      }
    } catch {}

    // 🔧 一致性校正：以实际在播ID对齐 currentIndex，防止“篡位”
    try {
      if (typeof (this as any).reconcileSubtitleWithAudible === "function") {
        (this as any).reconcileSubtitleWithAudible(roomId);
      }
    } catch {}
  });

  // 绑定完成回调：推进索引，并继续维持 WebAudio 调度
  player.setCompleteCallback(_index => {
    try {
      const state = this.roomStates.get(roomId);
      if (!state) return;
      // 在调度下一句之前，按模式延迟应用参数，避免句中突变导致抖动/重复
      try {
        const st = getRoomWAState(this, roomId);
        if (st && st.paramMode === "hidden") {
          try {
            st.player.setOverlap && st.player.setOverlap(0);
          } catch {}
          try {
            st.player.setFadeMs && st.player.setFadeMs(0);
          } catch {}
          try {
            (st as any).paramMode = undefined;
          } catch {}
        } else if (st && st.paramMode === "visible") {
          try {
            st.player.setOverlap && st.player.setOverlap(0.2);
          } catch {}
          try {
            st.player.setFadeMs && st.player.setFadeMs(180);
          } catch {}
          try {
            (st as any).paramMode = undefined;
          } catch {}
        }
      } catch {}
      try {
        const s = (getRoomWAState(this, roomId)?.player as any)?.getStatus?.();
        console.log(
          `[apple-wa] oncomplete idx=${_index} pending=${!!state.pendingProductSwitch} waStatus=${s ? JSON.stringify({ isPlaying: s.isPlaying, cur: s.currentTime?.toFixed?.(2), dur: s.duration?.toFixed?.(2) }) : "n/a"}`
        );
      } catch {}
      // 边界切换：若处于“准备切换”且目标已就绪，则在自然边界执行真实切换
      try {
        const wa = getRoomWAState(this, roomId) as any;
        const targetPid = wa?.preparingTargetPid || null;
        // 仅当“明确的目标就绪标记”为 true 才进行边界切换，避免误判
        const ready = !!(wa && wa.targetPrepared === true);
        if (targetPid && ready) {
          // 边界瞬间：防止旧内容继续被排程
          try {
            wa.player?.setSuppressAutoNext?.(true);
          } catch {}
          // 先停止“当前 index 条目”的非目标内容，避免继续读旧产品第二句
          try {
            (wa.player as any)?.stopCurrentIfDisallowed?.(targetPid);
          } catch {}
          // 然后限制并清理后续队列
          try {
            wa.player?.setAllowedProductId?.(targetPid);
            wa.player?.purgeQueuedExceptAllowed?.(true);
            // 标记边界刚完成切换，用于起播处兜底显示切换成功（即便 productId 缺失）
            try {
              (wa as any).justSwitchedAtBoundary = true;
              (wa as any).justSwitchPid = targetPid;
            } catch {}
          } catch {}
          state.isPlaying = false;
          try {
            this.executePendingProductSwitch &&
              this.executePendingProductSwitch(roomId);
            console.log(
              "[apple-wa] executePendingProductSwitch at boundary (prepared)"
            );
          } catch (e) {
            console.warn("[apple-wa] executePendingProductSwitch error", e);
          }
          // 切换完成：清理准备标记，恢复自动推进
          try {
            wa.preparingTargetPid = null;
            wa.awaitingTargetDecode = false;
          } catch {}
          try {
            wa.player?.setSuppressAutoNext?.(false);
          } catch {}
          return;
        }
      } catch {}

      // 标记当前项为已播放
      const cur = state.queue[state.currentIndex];
      if (cur) {
        cur.status = "played";
        cur.playedCharCount = cur.text?.length || 0;
        try {
          const waStateAfter = getRoomWAState(this, roomId) as any;
          if (waStateAfter && waStateAfter.firstAfterSwitchAwaitingComplete) {
            const expectedId = waStateAfter.firstAfterSwitchAudioId || null;
            const key = waStateAfter.firstAfterSwitchKey || null;
            const matches = !expectedId || (cur.id && cur.id === expectedId);
            if (matches && key) {
              try {
                waStateAfter.addedIds && waStateAfter.addedIds.add(key);
              } catch {}
            }
            if (matches) {
              waStateAfter.firstAfterSwitchAwaitingComplete = false;
              waStateAfter.firstAfterSwitchPending = false;
              waStateAfter.firstAfterSwitchKey = null;
              waStateAfter.firstAfterSwitchAudioId = null;
              waStateAfter.firstAfterSwitchPid = null;
              waStateAfter.justSwitchPid = null;
            }
          }
        } catch {}
      }
      try {
        const currentForLog = state.queue[state.currentIndex];
        const rawText = currentForLog?.text ?? "";
        logInfo(
          `complete(room=${roomId}) before-advance idx=${state.currentIndex} text="${rawText}"`
        );
      } catch {}

      // 获取 WebAudio player 的状态来确定实际的下一个索引
      try {
        const wa = getRoomWAState(this, roomId);
        const playerStatus = wa?.player?.getStatus?.();
        if (playerStatus && typeof playerStatus.currentIndex === "number") {
          // 如果当前播放项有 storeIndex，优先使用它来定位下一项
          const currentItem = playerStatus.currentItem as any;
          if (
            currentItem &&
            typeof currentItem.storeIndex === "number" &&
            currentItem.storeIndex >= 0
          ) {
            // 使用 storeIndex + 1 作为下一个索引
            state.currentIndex = currentItem.storeIndex + 1;
          } else {
            // 尝试通过 ID 查找当前项在队列中的位置
            if (currentItem?.id) {
              const idx = state.queue.findIndex(
                (x: any) => x && x.id === currentItem.id
              );
              if (idx >= 0) {
                state.currentIndex = idx + 1;
              } else {
                // 降级：简单递增
                state.currentIndex++;
              }
            } else {
              // 降级：简单递增
              state.currentIndex++;
            }
          }
        } else {
          // 降级方案：简单递增
          state.currentIndex++;
        }
      } catch {
        // 如果获取状态失败，降级为简单递增
        state.currentIndex++;
      }

      try {
        const next = state.queue[state.currentIndex];
        logInfo(
          `complete(room=${roomId}) after-advance idx=${state.currentIndex}/${state.queue.length} nextId=${next?.id || "NA"}`
        );
      } catch {}

      // 若还有后续项，保持 isPlaying 标志，由外部 feed 继续追加；否则视为停止
      if (state.currentIndex >= state.queue.length) {
        state.isPlaying = false;
        logInfo(`queue drained room=${roomId}`);
      } else {
        state.isPlaying = true;
        // 注释索引推进日志，减少日志输出
        // logInfo(`advance room=${roomId} => idx=${state.currentIndex}/${state.queue.length}`);
        // 句间刷新“当前产品普通条目”的窗口：补充 pendingNormalFeed 中的条目
        try {
          const waState2: any = getRoomWAState(this, roomId);
          const p: any = waState2 && waState2.player;
          const status = p && p.getStatus && p.getStatus();
          const curIdx = Math.max(0, Number(status?.currentIndex || 0));
          const total = Math.max(0, Number(status?.totalItems || 0));
          let lookahead = Math.max(0, total - (curIdx + 1));
          const NORMAL_WINDOW = 3;

          while (
            waState2 &&
            waState2.pendingNormalFeed &&
            waState2.pendingNormalFeed.length > 0 &&
            lookahead < NORMAL_WINDOW
          ) {
            const it = waState2.pendingNormalFeed.shift();
            if (!it) break;
            try {
              const stRoom: any = this.roomStates.get(roomId);
              const ver = (() => {
                try {
                  const q =
                    stRoom && Array.isArray(stRoom.queue)
                      ? stRoom.queue.find((x: any) => x && x.id === it.id)
                      : null;
                  return (q as any)?.version ?? stRoom?.switchVersion ?? 0;
                } catch {
                  return 0;
                }
              })();
              const k = `${it.id}::${ver}`;
              if (waState2.addedIds && waState2.addedIds.has(k)) {
                try {
                  const txt = (it as any)?.text ?? "";
                  logWarn(
                    `[prefill-skip] reason=dedup room=${roomId} id=${it.id} key=${k} text=${JSON.stringify(txt)}`
                  );
                } catch {}
                continue;
              }
              try {
                p && p.addToPlaylist && p.addToPlaylist(it);
                lookahead++;
                if (waState2.addedIds) waState2.addedIds.add(k);
                try {
                  const txt = (it as any)?.text ?? "";
                  logInfo(
                    `[prefill-add] room=${roomId} id=${it.id} text=${JSON.stringify(txt)}`
                  );
                } catch {}
              } catch {}
            } catch {
              try {
                p && p.addToPlaylist && p.addToPlaylist(it);
                lookahead++;
                try {
                  const txt = (it as any)?.text ?? "";
                  logInfo(
                    `[prefill-add-fallback] room=${roomId} id=${it.id} text=${JSON.stringify(txt)}`
                  );
                } catch {}
              } catch {}
            }
          }
        } catch {}
      }

      // 🍎 前台交接：若处于可见且 handoff 模式，当前句结束后由 Howler 接管下一句
      try {
        const st = getRoomWAState(this, roomId);
        const visible =
          typeof document !== "undefined" ? !document.hidden : true;
        if (st && st.foreHandoff && visible) {
          logInfo(
            `foreground handoff on complete -> playNext by Howler room=${roomId} cause=handoff_oncomplete`
          );
          // 解锁+标记交互，降低 NotAllowed
          try {
            (globalThis as any).HowlerSeamlessPlayer?.unlock &&
              (globalThis as any).HowlerSeamlessPlayer.unlock();
          } catch {}
          try {
            (this as any).userHasInteracted = true;
            localStorage.setItem("chrome_autoplay_explicitly_set", "true");
          } catch {}
          // 触发 Howler 下一句（先解冻，再播放）
          try {
            const state = this.roomStates.get(roomId);
            const hp: any = state && (state as any).howlerPlayer;
            if (hp) {
              if (typeof hp.setAppleHiddenFreeze === "function") {
                hp.setAppleHiddenFreeze(false);
              }
              try {
                typeof hp.setMuted === "function" && hp.setMuted(false);
              } catch {}
              try {
                typeof hp.setVolume === "function" && hp.setVolume(1);
              } catch {}
              try {
                typeof hp.setWaitingForProgressRestore === "function" &&
                  hp.setWaitingForProgressRestore(false);
              } catch {}
              try {
                typeof hp.suppressForegroundForcedComplete === "function" &&
                  hp.suppressForegroundForcedComplete(1500);
              } catch {}
              try {
                const stAU =
                  typeof hp.getStatus === "function" ? hp.getStatus() : null;
                logInfo(`handoff howler ready status room=${roomId}`, stAU);
              } catch {}
            }
          } catch {}
          // 交接播放前，设置一次性“无进度恢复”标志，并清除全局 handoff 激活标记，避免统一层抑制 Howler 启播
          try {
            const state = this.roomStates.get(roomId);
            if (state) {
              (state as any).handoffNoRestore = true;
            }
          } catch {}
          try {
            const g: any = globalThis as any;
            g.__appleHandoffActiveRooms &&
              g.__appleHandoffActiveRooms.delete(roomId);
          } catch {}
          try {
            this.playNext && this.playNext(roomId);
            logInfo(
              `foreground handoff playNext called room=${roomId} cause=handoff_oncomplete`
            );
          } catch (e) {
            logWarn(`handoff playNext error room=${roomId}`, e);
          }

          // 事件化销毁：若 Howler 已可听（howlerAudible=true），此 oncomplete 即销毁 WA 分支
          try {
            const stNow = getRoomWAState(this, roomId);
            if (
              stNow &&
              (stNow as any).foreHandoff &&
              (stNow as any).howlerAudible
            ) {
              try {
                stNow.player.stop();
                stNow.player.destroy();
              } catch {}
              setRoomWAState(this, roomId, null);
              try {
                const g: any = globalThis as any;
                g.__appleHandoffActiveRooms &&
                  g.__appleHandoffActiveRooms.delete(roomId);
              } catch {}
              try {
                (st as any)._vhUnbind && (st as any)._vhUnbind();
                (st as any)._vhUnbind = undefined;
              } catch {}
              logInfo(
                `visible handoff: WA destroyed on oncomplete (howler audible) room=${roomId}`
              );
            }
          } catch {}
        }
      } catch {}

      // 🍎 隐藏态预填扩展：将“扩大预填（1→3）”移动到句间（onended）执行，完全句间化
      try {
        const st = getRoomWAState(this, roomId) as any;
        const isHidden =
          typeof document !== "undefined" ? document.hidden : false;
        const waOnly = (() => {
          try {
            const g: any = (globalThis as any).__appleUseWAOnlyRooms;
            return !!(g && typeof g.has === "function" && g.has(roomId));
          } catch {
            return false;
          }
        })();
        if (st && waOnly) {
          const state2 = this.roomStates.get(roomId);
          if (state2 && Array.isArray(state2.queue)) {
            const start = Math.min(
              state2.queue.length,
              Math.max(0, state2.currentIndex + 1)
            );
            const extra = isHidden ? 2 : 1; // 隐藏态更激进，前台保守
            const end = Math.min(state2.queue.length, start + extra);
            if (end > start) {
              logInfo(
                `prefill(onended) room=${roomId} mode=${isHidden ? "hidden" : "visible"} queueRange=[${start}, ${end}) len=${state2.queue.length}`
              );
              for (let i = start; i < end; i++) {
                const it = state2.queue[i];
                if (!it?.audioUrl || !it?.id) continue;
                const pid = (it as any)?.productId || "NA";
                const dedupKey = `${pid}::${it.id}::${(it as any)?.version ?? state2.switchVersion ?? 0}`;
                const already = !!(
                  (st as any)?.addedIds && (st as any).addedIds.has(dedupKey)
                );
                if (already) {
                  logInfo(
                    `prefill(onended-dedup-skip) room=${roomId} id=${it.id} idx=${i}`
                  );
                  continue;
                }
                const waItem: WebAudioItem = {
                  id: it.id,
                  text: it.text || "",
                  audioUrl: it.audioUrl
                };
                try {
                  st.player.addToPlaylist(waItem);
                  try {
                    (st as any).addedIds && (st as any).addedIds.add(dedupKey);
                  } catch {}
                } catch (e) {
                  logError(
                    `prefill(onended) addToPlaylist error idx=${i} room=${roomId}`,
                    e
                  );
                }
              }
            }
          }
        }
      } catch {}
    } catch {}
  });

  // 边界修正：若当前条已达末尾且未在播，主动推进并尝试启动
  const tryBoundaryFix = () => {
    try {
      const st = getRoomWAState(this, roomId);
      if (!st) return;
      // 切换瞬间：不做兜底，避免与“仅放声”目标冲突
      if (
        typeof st.switchFreezeUntilTs === "number" &&
        Date.now() < st.switchFreezeUntilTs
      ) {
        try {
          logInfo(
            `freeze: skip boundary-fix room=${roomId} untilTs=${st.switchFreezeUntilTs}`
          );
        } catch {}
        return;
      }
      // 若正在等待 Howler 的边界切换，则不执行 WA 的边界自启，避免双声
      if (st.awaitingHowlerBoundary) {
        return;
      }
      const s = st.player.getStatus();
      if (!s) return;
      if (!s.isPlaying) {
        // 大幅放宽判定条件，只要音频未播放就尝试播放
        // 移除idle限制，确保各种状态下都能触发播放
        const atEndOrUnknown = 
          s.duration === 0 ||
          (s.duration > 0 && s.currentTime >= s.duration - 0.01);
        const pendingSwitch = 
          !!this.roomStates.get(roomId)?.pendingProductSwitch;
        const inHandoff = !!(st as any).foreHandoff;
        const awaitingTarget = !!(st as any).awaitingTargetDecode;
        
        // 只要满足任一条件就尝试播放
        if (atEndOrUnknown || pendingSwitch || inHandoff || awaitingTarget) {
          try {
            logWarn(
              `boundary fix: current ended/idle but not playing room=${roomId}`
            );
            logInfo(`boundary fix diag room=${roomId}`, {
              s,
              flags: { pendingSwitch, inHandoff, awaitingTarget }
            });
          } catch {}
          // 添加播放列表检查，避免在列表为空时尝试播放
          try {
            if (s.totalItems > 0 && st.player && typeof st.player.play === 'function') {
              st.player.play();
              logInfo(`boundary fix: play() immediate room=${roomId} items=${s.totalItems}`);
            } else if (s.totalItems === 0) {
              logInfo(`boundary fix: skip - playlist empty room=${roomId}`);
            } else if (!st.player) {
              logWarn(`boundary fix: player not found room=${roomId}`);
            } else if (typeof st.player.play !== 'function') {
              logWarn(`boundary fix: player.play is not a function room=${roomId}`);
            }
          } catch {}
        }
      }
    } catch (e) {
      logWarn(`boundary fix error room=${roomId}`, e);
    }
  };
  // 定时做一次边界修正（低频，避免开销）；如已有旧定时器则先清理
  try {
    const existed = getRoomWAState(this, roomId);
    if (existed?.boundaryTimer) {
      try {
        clearInterval(existed.boundaryTimer);
      } catch {}
    }
  } catch {}

  const waState: RoomWebAudioState = {
    player,
    roomId,
    attached: false,
    _unbindFns: [],
    addedIds: new Set<string>(),
    idVersionMap: new Map<string, number>()
  };
  setRoomWAState(this, roomId, waState);

  // 附着 visibility 监听（仅一次）
  if (!waState.attached) {
    const handler = () => {
      const hidden = typeof document !== "undefined" ? document.hidden : false;
      try {
        const st = getRoomWAState(this, roomId);
        const s = st?.player?.getStatus && st.player.getStatus();
        logInfo(
          `visibilitychange hidden=${hidden} room=${roomId} status=${s ? JSON.stringify({ isPlaying: s.isPlaying, idx: s.currentIndex, active: (s as any).activeSourcesCount, t: s.currentTime?.toFixed?.(2), d: s.duration?.toFixed?.(2) }) : "n/a"}`
        );
      } catch {}
      if (!this.roomStates.has(roomId)) return;
      let st = getRoomWAState(this, roomId);
      if (hidden) {
        // 0ms 硬断开：在任何后续逻辑之前，立即物理断开输出，抑制接回窗口，彻底阻断系统缓存瞬时出声
        try {
          const stDet = getRoomWAState(this, roomId) as any;
          if (
            stDet &&
            stDet.player &&
            typeof stDet.player.detachOutput === "function"
          ) {
            try {
              (stDet.player as any).suppressAttachUntilTs = Date.now() + 150;
            } catch {}
            stDet.player.detachOutput();
            logInfo(`instant detach on hidden room=${roomId}`);
          }
        } catch {}
        // 切换瞬间冻结窗口（仅放声、搁置其它计算）
        try {
          const wa = getRoomWAState(this, roomId) as any;
          if (wa) {
            const first =
              typeof wa.firstSwitch === "undefined" ? true : !!wa.firstSwitch;
            wa.switchFreezeUntilTs = Date.now() + 380;
            wa._freezeProgressLogged = false;
            logInfo(
              `freeze: enter hidden window room=${roomId} untilTs=${wa.switchFreezeUntilTs}`
            );
            try {
              // 记录 Howler 当前状态，便于诊断后台交接点
              const state = this.roomStates.get(roomId);
              const hp: any = state && (state as any).howlerPlayer;
              if (hp && typeof hp.getStatus === "function") {
                const hs = hp.getStatus();
                logInfo(
                  `handoff(diag): hidden-enter howler status room=${roomId} playing=${hs?.isPlaying} cur=${hs?.currentTime?.toFixed?.(2)} dur=${hs?.duration?.toFixed?.(2)}`
                );
              }
            } catch {}
            // 延后到 ensureAppleWASink 之后再做输出断连（避免后续 ensure 立即把连接接回）
          }
        } catch {}
        // 注释调试探针日志，减少日志输出
        // try { console.warn(`[probe] WA_VISIBILITY_HIDDEN_ENTER room=${roomId}`); } catch {}
        // 隐藏：如尚未创建，则立即创建并预填充后续队列
        if (!st) {
          try {
            (this as any).ensureAppleHiddenWebAudio &&
              (this as any).ensureAppleHiddenWebAudio(roomId);
          } catch {}
          st = getRoomWAState(this, roomId);
          // 注释可见性变化日志，减少日志输出
          // logInfo(`visibility hidden -> ensure WA player room=${roomId}, created=${!!st}`);
        }
        if (!st) return;
        // 隐藏：确保 AC 运行，便于后续解码与调度；并确保绑定 <audio> sink 以维持后台媒体会话
        try {
          st.player.unlock();
          const acState = (st.player as any)?.ac?.state;
          // 注释音频上下文解锁日志，减少日志输出
          // logInfo(`visibility hidden -> unlock AC room=${roomId} state=${acState || "n/a"}`);
        } catch (e) {
          logWarn(`unlock failed room=${roomId}`, e);
        }
        try {
          (this as any).ensureAppleWASink &&
            (this as any).ensureAppleWASink(roomId);
        } catch {}
        try {
          const el: any = getRoomWAState(this, roomId)?.sinkEl;
          if (el) {
            const paused = !!el.paused;
            logInfo(`freeze: sink state paused=${paused}`);
            if (paused) {
              el.play()
                .then(() => logInfo(`freeze: sink play() resume ok`))
                .catch((e: any) =>
                  logWarn(`freeze: sink play() resume failed`, e)
                );
            }
          }
        } catch {}
        // 🍎 关键：在确保 sink 存在后，立即物理断开输出，100ms 后若仍在后台再恢复
        try {
          const stNow = getRoomWAState(this, roomId) as any;
          if (
            stNow &&
            stNow.player &&
            typeof stNow.player.detachOutput === "function"
          ) {
            stNow.player.detachOutput();
            try {
              if (stNow._bgUnmuteTimer) {
                clearTimeout(stNow._bgUnmuteTimer);
              }
            } catch {}
            stNow._bgUnmuteTimer = window.setTimeout(() => {
              try {
                const stillHidden =
                  typeof document !== "undefined" ? document.hidden : false;
                if (!stillHidden) return;
                const stLate = getRoomWAState(this, roomId) as any;
                if (
                  stLate &&
                  stLate.player &&
                  typeof stLate.player.attachOutput === "function"
                ) {
                  stLate.player.attachOutput();
                  logInfo(`bg auto-attach after 100ms room=${roomId}`);
                }
              } catch (e) {
                logWarn(`bg auto-attach failed room=${roomId}`, e);
              }
            }, 100);
            logInfo(
              `bg detach output on hidden(room) after ensure sink room=${roomId}`
            );
          }
        } catch {}
        // 冻结当前源增益，并进行轻度抑制（可见->隐藏：减轻瞬间伪影）
        try {
          const stNow = getRoomWAState(this, roomId) as any;
          if (stNow && (stNow.player as any).freezeCurrentGain) {
            (stNow.player as any).freezeCurrentGain(0.05);
          }
          if (stNow && (stNow.player as any).pulseFreezeGuard) {
            (stNow.player as any).pulseFreezeGuard(120, 0.78);
          }
          if (stNow && (stNow.player as any).boostCompressor) {
            (stNow.player as any).boostCompressor(320, false);
          }
        } catch {}
        // 延迟到“下一句”才应用隐藏态参数，避免句中突变
        try {
          const st0 = getRoomWAState(this, roomId);
          if (st0) (st0 as any).paramMode = "hidden";
        } catch {}
        // 预填充：将当前索引之后的若干条（窗口）加入 WebAudio 播放列表（延迟到切换窗口后再执行）
        try {
          const state = this.roomStates.get(roomId);
          if (state && Array.isArray(state.queue)) {
            // 告知 Howler 在隐藏态冻结（不继续下一条）
            try {
              const hp: any = (state as any).howlerPlayer;
              if (hp && typeof hp.setAppleHiddenFreeze === "function") {
                hp.setAppleHiddenFreeze(true);
                logInfo(`freeze howler auto-next in hidden room=${roomId}`);
                // 标记等待 Howler 到达边界，避免 WA 提前起播与双声
                try {
                  const stMark = getRoomWAState(this, roomId) as any;
                  const sHp =
                    typeof hp.getStatus === "function" ? hp.getStatus() : null;
                  const hpPlaying = !!(sHp && sHp.isPlaying);
                  if (stMark) stMark.awaitingHowlerBoundary = hpPlaying;
                  if (hpPlaying)
                    logInfo(
                      `await howler boundary: set awaitingHowlerBoundary=true room=${roomId}`
                    );
                } catch {}
              }
            } catch {}
            // 切换窗口内：不做任何索引重排/Howler 预热等可能触发重排的操作
            // 在冻结窗口结束后再预填 + 自启检查，避开 iOS 切换窗口
            try {
              if ((st as any)._hiddenPrefillTimer) {
                clearTimeout((st as any)._hiddenPrefillTimer);
              }
            } catch {}
            const waNow: any = getRoomWAState(this, roomId);
            const now = Date.now();
            const delayMs = Math.max(
              80,
              waNow && waNow.switchFreezeUntilTs
                ? waNow.switchFreezeUntilTs - now + 20
                : 180
            );
            (st as any)._hiddenPrefillTimer = window.setTimeout(() => {
              try {
                // 若仍在冻结窗口内，进一步延后 80ms
                try {
                  const wa = getRoomWAState(this, roomId) as any;
                  if (
                    wa &&
                    wa.switchFreezeUntilTs &&
                    Date.now() < wa.switchFreezeUntilTs
                  ) {
                    wa.switchFreezeUntilTs += 80;
                    logInfo(
                      `freeze: extend window +80ms untilTs=${wa.switchFreezeUntilTs}`
                    );
                  }
                } catch {}
                const start = Math.min(
                  state.queue.length,
                  Math.max(0, state.currentIndex + 1)
                );
                let end = Math.min(state.queue.length, start + 5);
                try {
                  const g: any = (globalThis as any).__appleUseWAOnlyRooms;
                  const waOnly = !!(
                    g &&
                    typeof g.has === "function" &&
                    g.has(roomId)
                  );
                  if (waOnly) {
                    const st2 = getRoomWAState(this, roomId) as any;
                    if (st2 && !st2.hiddenInitPrefilled) {
                      end = Math.min(state.queue.length, start + 1);
                      st2.hiddenInitPrefilled = true;
                    } else {
                      end = Math.min(state.queue.length, start + 3);
                    }
                  }
                } catch {}
                logInfo(
                  `prefill(delayed) room=${roomId} queueRange=[${start}, ${end}) len=${state.queue.length}`
                );
                for (let i = start; i < end; i++) {
                  const it = state.queue[i];
                  if (!it?.audioUrl || !it?.id) continue;
                  const pid2 = (it as any)?.productId || "NA";
                  const dedupKey2 = `${pid2}::${it.id}::${(it as any)?.version ?? state.switchVersion ?? 0}`;
                  const already = !!(
                    (st as any)?.addedIds && (st as any).addedIds.has(dedupKey2)
                  );
                  if (already) {
                    logInfo(
                      `prefill(dedup-skip) room=${roomId} id=${it.id} idx=${i}`
                    );
                    continue;
                  }
                  const waItem: WebAudioItem = {
                    id: it.id,
                    text: it.text || "",
                    audioUrl: it.audioUrl
                  };
                  try {
                    st.player.addToPlaylist(waItem);
                    try {
                      (st as any).addedIds &&
                        (st as any).addedIds.add(dedupKey2);
                    } catch {}
                  } catch (e) {
                    logError(
                      `prefill addToPlaylist error idx=${i} room=${roomId}`,
                      e
                    );
                  }
                }
                try {
                  const g: any = globalThis as any;
                  const waOnlySet = g && g.__appleUseWAOnlyRooms;
                  const waOnly = !!(
                    waOnlySet &&
                    typeof waOnlySet.has === "function" &&
                    waOnlySet.has(roomId)
                  );
                  const status = st.player.getStatus && st.player.getStatus();
                  if (waOnly && status && !status.isPlaying) {
                    // 仅在 Howler 已结束或接近尾声时，才允许 WA 起播（避免句尾重叠与乱序）
                    let allowStart = true;
                    try {
                      const state2 = this.roomStates.get(roomId);
                      const hp: any = state2 && (state2 as any).howlerPlayer;
                      if (hp && typeof hp.getStatus === "function") {
                        const hs = hp.getStatus();
                        const nearEnd =
                          hs &&
                          hs.duration > 0 &&
                          hs.currentTime >= Math.max(0, hs.duration - 0.25);
                        const actuallyPlaying = !!(hs && hs.isPlaying);
                        const awaiting = !!(getRoomWAState(this, roomId) as any)
                          ?.awaitingHowlerBoundary;
                        logInfo(
                          `handoff(diag): prefill gate check room=${roomId} awaiting=${awaiting} hpPlaying=${actuallyPlaying} nearEnd=${nearEnd} hs=${hs?.currentTime?.toFixed?.(2)}/${hs?.duration?.toFixed?.(2)}`
                        );
                        // 若仍在等待 Howler 边界且 Howler 还在播且未到尾声，则暂不启动
                        if (awaiting && actuallyPlaying && !nearEnd) {
                          allowStart = false;
                          logInfo(
                            `gated WA start: awaiting howler boundary (cur=${hs.currentTime?.toFixed?.(2)}/${hs.duration?.toFixed?.(2)}) room=${roomId}`
                          );
                        }
                      }
                    } catch {}
                    if (allowStart) {
                      logInfo(
                        `handoff(start): WA play() by prefill gate room=${roomId}`
                      );
                      st.player.setMuted && st.player.setMuted(false);
                      st.player.play && st.player.play();
                      st.awaitingHowlerBoundary = false;
                    }
                  }
                } catch {}
              } catch {}
            }, delayMs) as unknown as number;
          }
        } catch (e) {
          logWarn(`prefill failed room=${roomId}`, e);
        }
      } else {
        // 切回前台冻结窗口，避免句中抖动
        try {
          const wa = getRoomWAState(this, roomId) as any;
          if (wa) {
            const first =
              typeof wa.firstSwitch === "undefined" ? true : !!wa.firstSwitch;
            wa.switchFreezeUntilTs = Date.now() + 420;
            wa._freezeProgressLogged = false;
            logInfo(
              `freeze: enter visible window room=${roomId} untilTs=${wa.switchFreezeUntilTs}`
            );
            // 第一次切换窗口结束后，标记 firstSwitch=false
            window.setTimeout(
              () => {
                try {
                  wa.firstSwitch = false;
                } catch {}
              },
              Math.max(0, Math.floor(wa.switchFreezeUntilTs - Date.now() + 50))
            );
          }
        } catch {}
        // 可见窗口：恢复为强抑制，但减小可见瞬间的瞬变伪影（采用更轻的freeze/pulse）
        try {
          const stNow = getRoomWAState(this, roomId);
          if (stNow && (stNow.player as any).freezeCurrentGain) {
            (stNow.player as any).freezeCurrentGain(0.05);
          }
          if (stNow && (stNow.player as any).pulseFreezeGuard) {
            (stNow.player as any).pulseFreezeGuard(80, 0.85);
          }
          if (stNow && (stNow.player as any).boostCompressor) {
            // 可见侧仍使用强压，但时长与可见窗口一致，提升稳定性
            (stNow.player as any).boostCompressor(380, true);
          }
        } catch {}
        // 可见：统一 WA→WA（不交接 Howler）。保持 WA 播放，并做 UI/索引同步与边界修正。
        // 交接前，确保 AudioContext 处于 running，避免回前台后无声
        try {
          st.player.unlock && st.player.unlock();
        } catch {}
        try {
          st.player.setForeHandoff && st.player.setForeHandoff(false);
        } catch {}
        // 可见：清理后台自动恢复定时器并确保输出连通
        try {
          const stNow = getRoomWAState(this, roomId) as any;
          if (stNow && stNow._bgUnmuteTimer) {
            clearTimeout(stNow._bgUnmuteTimer);
            stNow._bgUnmuteTimer = undefined;
          }
          if (
            stNow &&
            stNow.player &&
            typeof stNow.player.attachOutput === "function"
          ) {
            stNow.player.attachOutput();
          }
        } catch {}
        // 冻结窗口内不做 UI 同步/索引修正/起播，避免句中重排
        try {
          const wa = getRoomWAState(this, roomId) as any;
          if (
            wa &&
            typeof wa.switchFreezeUntilTs === "number" &&
            Date.now() < wa.switchFreezeUntilTs
          ) {
            logInfo(`freeze: skip visible UI sync room=${roomId}`);
            return;
          }
        } catch {}
        // 恢复前台参数延迟到下一句应用，避免句中突变
        try {
          const st0 = getRoomWAState(this, roomId);
          if (st0) (st0 as any).paramMode = "visible";
        } catch {}
        // 可见侧兜底起播：窗口外再评估，且仅在 Howler 已结束或近尾声时才允许 WA.play()
        try {
          const wa = getRoomWAState(this, roomId) as any;
          if (!wa) return;
          const s = wa.player.getStatus && wa.player.getStatus();
          if (!s || !s.isPlaying) {
            let allowStart = true;
            try {
              const state2 = this.roomStates.get(roomId);
              const hp: any = state2 && (state2 as any).howlerPlayer;
              const awaiting = !!(getRoomWAState(this, roomId) as any)
                ?.awaitingHowlerBoundary;
              if (hp && typeof hp.getStatus === "function") {
                const hs = hp.getStatus();
                const nearEnd =
                  hs &&
                  hs.duration > 0 &&
                  hs.currentTime >= Math.max(0, hs.duration - 0.25);
                const actuallyPlaying = !!(hs && hs.isPlaying);
                logInfo(
                  `handoff(diag): visible gate check room=${roomId} awaiting=${awaiting} hpPlaying=${actuallyPlaying} nearEnd=${nearEnd} hs=${hs?.currentTime?.toFixed?.(2)}/${hs?.duration?.toFixed?.(2)}`
                );
                if (awaiting && actuallyPlaying && !nearEnd) {
                  allowStart = false;
                  logInfo(
                    `gated WA start(visible): awaiting howler boundary room=${roomId}`
                  );
                }
              }
            } catch {}
            if (allowStart) {
              // 轻防抖：避免极端时序在短时间内重复触发起播
              const nowTs = Date.now();
              const stMark = getRoomWAState(this, roomId) as any;
              const lastTs = stMark?.lastVisiblePlayTs || 0;
              if (!lastTs || nowTs - lastTs > 600) {
                wa.player.play && wa.player.play();
                try {
                  if (stMark) {
                    stMark.awaitingHowlerBoundary = false;
                    stMark.lastVisiblePlayTs = nowTs;
                  }
                } catch {}
              } else {
                logInfo(
                  `visible play debounce: skip (dt=${nowTs - lastTs}ms) room=${roomId}`
                );
              }
            }
          }
        } catch {}
        // 不再设置 handoffActive；Howler 不参与可见态播放
        // 在交接前拷贝一次精确进度，避免 100% 恢复导致瞬间完成
        let waStatusForVisible: any = null;
        try {
          waStatusForVisible = st.player.getStatus();
          const state = this.roomStates.get(roomId);
          if (
            state &&
            waStatusForVisible &&
            waStatusForVisible.duration > 0 &&
            state.queue[state.currentIndex]
          ) {
            const p = Math.max(
              0,
              Math.min(
                1,
                waStatusForVisible.currentTime / waStatusForVisible.duration
              )
            );
            const clamp = p >= 0.98 ? 0.96 : p; // 防止100%恢复引发立刻结束
            (state.queue[state.currentIndex] as any).audioTimeProgress = clamp;
            (state.queue[state.currentIndex] as any).audioCurrentTime =
              clamp * waStatusForVisible.duration;
            (state.queue[state.currentIndex] as any).audioDuration =
              waStatusForVisible.duration;
            logInfo(
              `visible ui-prepare: room=${roomId} idx=${state.currentIndex}/${state.queue.length} p=${(clamp * 100).toFixed(1)}%`
            );
          }
        } catch {}
        // UI 同步优先按 itemId 对齐；找不到时再回退 base+waIndex
        try {
          const st2 = getRoomWAState(this, roomId);
          const s2 = st2?.player.getStatus();
          const state2 = this.roomStates.get(roomId);
          let mappedIdx = -1;
          if (s2 && s2.currentItem && state2 && Array.isArray(state2.queue)) {
            const curId = (s2.currentItem as any).id;
            mappedIdx = state2.queue.findIndex((x: any) => x && x.id === curId);
          }
          if (mappedIdx >= 0 && state2 && state2.currentIndex !== mappedIdx) {
            logInfo(
              `visible UI sync by id: room=${roomId} storeIdx=${state2.currentIndex} -> ${mappedIdx} id=${(s2 as any)?.currentItem?.id}`
            );
            state2.currentIndex = mappedIdx;
          } else {
            const base = (st2 as any)?.baseStoreIndex ?? 0;
            if (s2 && typeof base === "number" && base >= 0) {
              const fallback = Math.min(
                (state2?.queue?.length ?? 0) - 1,
                Math.max(0, base + (s2.currentIndex || 0))
              );
              if (state2 && state2.currentIndex !== fallback) {
                logInfo(
                  `visible UI sync: room=${roomId} storeIdx=${state2.currentIndex} -> ${fallback} (base=${base}, waIdx=${s2.currentIndex})`
                );
                state2.currentIndex = fallback;
              }
            }
          }
        } catch {}
        // 不再预加载 Howler、也不做交接销毁；保留一次互斥检查
        try {
          ensureSingleAudible.call(this, roomId);
        } catch {}
        return;
      }
    };
    try {
      document.addEventListener("visibilitychange", handler, { passive: true });
    } catch {}
    try {
      waState.visibilityHandler = handler;
    } catch {}
    waState.attached = true;
    // 预隐藏/预可见：在 hidden/visible 之前的早期信号上做一次性轻抑制与冻结，专治首次切换
    try {
      const preHidden = (ev: Event) => {
        try {
          const hidden =
            typeof document !== "undefined" ? document.hidden : false;
          if (hidden) return; // 已 hidden 的情况交给 visibilitychange
          const st0 = getRoomWAState(this, roomId) as any;
          if (!st0) return;
          const first =
            typeof st0.firstSwitch === "undefined" ? true : !!st0.firstSwitch;
          const nowTs = Date.now();
          const until = nowTs + 420;
          st0.switchFreezeUntilTs = until;
          st0._freezeProgressLogged = false;
          logInfo(`freeze: pre-hidden guard room=${roomId} untilTs=${until}`);
          try {
            (st0.player as any).freezeCurrentGain &&
              (st0.player as any).freezeCurrentGain(0.09);
          } catch {}
          try {
            (st0.player as any).pulseFreezeGuard &&
              (st0.player as any).pulseFreezeGuard(180, 0.7);
          } catch {}
          try {
            (st0.player as any).boostCompressor &&
              (st0.player as any).boostCompressor(420, true);
          } catch {}
          // 0ms 硬断开（预隐藏阶段）：先物理断开输出 + 抑制接回，确保切换前沿无声
          try {
            if (
              st0 &&
              st0.player &&
              typeof st0.player.detachOutput === "function"
            ) {
              try {
                (st0.player as any).suppressAttachUntilTs = Date.now() + 150;
              } catch {}
              st0.player.detachOutput();
              logInfo(`instant detach on preHidden room=${roomId}`);
            }
          } catch {}
          // 轻量预热，避免首次后台 AC 切换抖动
          try {
            (st0.player as any).startKeepAlive &&
              (st0.player as any).startKeepAlive();
          } catch {}
        } catch {}
      };
      const preVisible = (ev: Event) => {
        try {
          const hidden =
            typeof document !== "undefined" ? document.hidden : false;
          if (!hidden) {
            const st0 = getRoomWAState(this, roomId) as any;
            if (!st0) return;
            const nowTs = Date.now();
            const until = nowTs + 380;
            st0.switchFreezeUntilTs = until;
            st0._freezeProgressLogged = false;
            logInfo(
              `freeze: pre-visible guard room=${roomId} untilTs=${until}`
            );
            try {
              (st0.player as any).freezeCurrentGain &&
                (st0.player as any).freezeCurrentGain(0.05);
            } catch {}
            try {
              (st0.player as any).pulseFreezeGuard &&
                (st0.player as any).pulseFreezeGuard(80, 0.85);
            } catch {}
            // 可见前强压：与窗口一致时长
            try {
              (st0.player as any).boostCompressor &&
                (st0.player as any).boostCompressor(380, true);
            } catch {}
          }
        } catch {}
      };
      window.addEventListener("blur", preHidden, { passive: true });
      window.addEventListener("pagehide", preHidden, { passive: true });
      window.addEventListener("focus", preVisible, { passive: true });
      window.addEventListener("pageshow", preVisible, { passive: true });
      waState.blurHandler = preHidden;
      waState.pagehideHandler = preHidden;
      waState.focusHandler = preVisible;
      waState.pageshowHandler = preVisible;
      try {
        waState._unbindFns &&
          waState._unbindFns.push(() => {
            try {
              window.removeEventListener("blur", preHidden);
            } catch {}
          });
      } catch {}
      try {
        waState._unbindFns &&
          waState._unbindFns.push(() => {
            try {
              window.removeEventListener("pagehide", preHidden);
            } catch {}
          });
      } catch {}
      try {
        waState._unbindFns &&
          waState._unbindFns.push(() => {
            try {
              window.removeEventListener("focus", preVisible);
            } catch {}
          });
      } catch {}
      try {
        waState._unbindFns &&
          waState._unbindFns.push(() => {
            try {
              window.removeEventListener("pageshow", preVisible);
            } catch {}
          });
      } catch {}
    } catch {}
    // 启动边界修正定时器（保存引用，便于清理）
    try {
      waState.boundaryTimer = window.setInterval(tryBoundaryFix, 1000);
    } catch {}

    // 🍎 监听 Howler 在隐藏态真正 ended 的事件，作为 WA 起播信号，避免双声
    const onHowlerHiddenEnded = (payload: any) => {
      try {
        if (!payload || payload.roomId !== roomId) return;
        const st = getRoomWAState(this, roomId);
        if (!st) return;
        const s = st.player.getStatus();
        if (!s.isPlaying) {
          // try { console.warn(`[probe] WA_START_REASON=HOWLER_ENDED room=${roomId}`); } catch {}
          logInfo(`signal: howler hidden ended -> WA play() room=${roomId}`);
          // 对齐 WA 播放索引为（主队列索引 - baseStoreIndex）
          try {
            const state = this.roomStates.get(roomId);
            const base =
              (st as any).baseStoreIndex ?? (state?.currentIndex ?? 0) + 1;
            const mapped = Math.max(0, (state?.currentIndex ?? 0) - base);
            if (
              state &&
              typeof (st.player as any).setCurrentIndex === "function"
            ) {
              (st.player as any).setCurrentIndex(mapped);
              logInfo(
                `align WA index on event room=${roomId} base=${base} storeIdx=${state.currentIndex} waIdx=${mapped}`
              );
            }
          } catch {}
          try {
            st.player.play();
          } catch {}
          st.awaitingHowlerBoundary = false;
          if (st.deferredStartTimer) {
            try {
              clearInterval(st.deferredStartTimer);
            } catch {}
            st.deferredStartTimer = undefined;
          }
        }
      } catch {}
    };
    try {
      emitter.on("apple:hidden-howler-ended", onHowlerHiddenEnded as any);
    } catch {}
    try {
      waState._unbindFns &&
        waState._unbindFns.push(() => {
          try {
            (emitter as any).off &&
              (emitter as any).off(
                "apple:hidden-howler-ended",
                onHowlerHiddenEnded
              );
          } catch {}
        });
    } catch {}
  }
}

/**
 * 确保为房间创建并绑定一个隐藏的 <audio> 元素作为 WebAudio 输出接收端（iOS 后台播放所需）。
 * 需在用户手势上下文内调用，以避免 play() 被拦截。
 */
export function ensureAppleWASink(this: Store, roomId: string) {
  const st = getRoomWAState(this, roomId);
  if (!st || !st.player) return;
  try {
    const sinkId = `wa_sink_${roomId}`;
    let el = document.getElementById(sinkId) as HTMLAudioElement | null;
    if (!el) {
      el = document.createElement("audio");
      el.id = sinkId;
      el.setAttribute("playsinline", "true");
      el.setAttribute("webkit-playsinline", "true");
      el.preload = "auto";
      el.loop = false; // 对于 MediaStream 不需要 loop，避免某些实现的寻址抖动
      el.style.display = "none";
      // 音量由 WA 控制增益；这里保持可听，避免被系统静音
      try {
        el.volume = 1;
      } catch {}
      document.body.appendChild(el);
    }
    // 绑定 WebAudio 输出到该 <audio>
    try {
      st.player.attachToElement(el);
    } catch {}
    // 立即播放，建立媒体会话（需处于用户手势内）
    el.play().catch(() => {});
    st.sinkEl = el;
  } catch {}
}

/** 在隐藏态将音频加入 WebAudio 播放列表（仅 iOS / hidden 时有效） */
export function feedAppleHiddenWebAudio(
  this: Store,
  roomId: string,
  legacyItem: {
    id: string;
    text: string;
    audioUrl: string;
    voiceNo?: number;
    productId?: string;
  }
) {
  if (!roomId || !this.roomStates.has(roomId)) return;
  const hidden = typeof document !== "undefined" ? document.hidden : false;
  // 苹果端：前台/后台都投喂 WA（统一 WA→WA）
  if (!(isIOSLike() || isSafari())) return;

  // 确保有 player
  this.ensureAppleHiddenWebAudio && this.ensureAppleHiddenWebAudio(roomId);
  const wa = getRoomWAState(this, roomId);
  if (!wa || !wa.player) return;

  const item: WebAudioItem = {
    id: legacyItem.id,
    text: legacyItem.text,
    audioUrl: legacyItem.audioUrl,
    voiceNo: (legacyItem as any).voiceNo,
    productId: (legacyItem as any).productId
  };
  try {
    const st = this.roomStates.get(roomId);
    if (st && Array.isArray(st.queue)) {
      item.storeIndex = st.queue.findIndex((x: any) => x && x.id === item.id);
    }
  } catch {}
  try {
    logInfo(`feed WA room=${roomId} id=${item.id} url=${item.audioUrl}`);
    // 如尚未设置基准，按（currentIndex+1）建立基准映射
    try {
      const g: any = globalThis as any;
      const waOnly = !!(
        g &&
        g.__appleUseWAOnlyRooms &&
        typeof g.__appleUseWAOnlyRooms.has === "function" &&
        g.__appleUseWAOnlyRooms.has(roomId)
      );
      if (!waOnly) {
        const state = this.roomStates.get(roomId);
        if (state && typeof (wa as any).baseStoreIndex !== "number") {
          (wa as any).baseStoreIndex = Math.min(
            (state.currentIndex ?? 0) + 1,
            state.queue?.length ?? 0
          );
          logInfo(
            `init WA baseStoreIndex on feed room=${roomId} base=${(wa as any).baseStoreIndex}`
          );
        }
      }
    } catch {}
    // 去重：若已加入 WA 列表则跳过（避免多轮 prefill/feed 的重复）
    // 使用版本化键：id::version（version 来源于队列项或房间 switchVersion）
    let _ver: number = 0;
    try {
      const stRoom: any = this.roomStates.get(roomId);
      const q =
        stRoom && Array.isArray(stRoom.queue)
          ? stRoom.queue.find((x: any) => x && x.id === item.id)
          : null;
      _ver = (q as any)?.version ?? stRoom?.switchVersion ?? 0;
    } catch {
      _ver = 0;
    }
    const waAny: any = wa as any;
    // 使用与addAudioToQueue一致的复合去重键格式：pid::id::version
    const pid = item.productId || "NA";
    const _dedupKey = `${pid}::${item.id}::${_ver}`;
    const targetPidFromSwitch = waAny?.justSwitchPid || null;
    const lockPid = (() => {
      const byBoundary = waAny?.justSwitchPid || null;
      if (byBoundary) return byBoundary;
      if (
        waAny?.firstAfterSwitchAwaitingComplete &&
        waAny?.firstAfterSwitchPid
      ) {
        return waAny.firstAfterSwitchPid;
      }
      return null;
    })();
    const isPotentialFirstAfterSwitch = !!(
      waAny &&
      waAny.justSwitchedAtBoundary &&
      targetPidFromSwitch &&
      item.productId &&
      item.productId === targetPidFromSwitch
    );
    const isUrgent = (item as any)?.voiceNo === -1;
    if (lockPid && item.productId && item.productId !== lockPid && !isUrgent) {
      const guardKey = "__switchGuard";
      const guard = (waAny[guardKey] = waAny[guardKey] || {
        start: Date.now(),
        count: 0
      });
      guard.count += 1;
      const elapsed = Date.now() - guard.start;
      const GUARD_TIMEOUT = Number(
        (waAny as any).switchFallbackTimeout ?? 2000
      );
      const GUARD_COUNT_LIMIT = Number(
        (waAny as any).switchFallbackMaxSkips ?? 2
      );
      if (elapsed > GUARD_TIMEOUT || guard.count >= GUARD_COUNT_LIMIT) {
        logWarn(
          `[switch-fallback] releasing product lock after ${guard.count} skips elapsed=${elapsed}ms room=${roomId} lockPid=${lockPid}`
        );
        try {
          waAny.justSwitchPid = null;
          waAny.firstAfterSwitchPending = false;
          waAny.firstAfterSwitchAwaitingComplete = false;
          waAny.firstAfterSwitchKey = null;
          waAny.firstAfterSwitchAudioId = null;
          waAny.firstAfterSwitchPid = null;
        } catch {}
        waAny[guardKey] = null;
        waAny.preparingTargetPid = null;
        waAny.targetPrepared = false;
        waAny.switchTargetReady = false;
        logInfo(
          `[switch-fallback] allow legacy item room=${roomId} id=${item.id} pid=${item.productId}`
        );
      } else {
        logInfo(
          `[switch-guard] skip post-switch item room=${roomId} id=${item.id} pid=${item.productId} expectPid=${lockPid} count=${guard.count} elapsed=${elapsed}ms`
        );
        return;
      }
    } else if (!lockPid || (item.productId && item.productId === lockPid)) {
      try {
        if (waAny.__switchGuard) waAny.__switchGuard = null;
      } catch {}
    }
    if (
      isPotentialFirstAfterSwitch &&
      !waAny.firstAfterSwitchPending &&
      !waAny.firstAfterSwitchAwaitingComplete
    ) {
      waAny.firstAfterSwitchPending = true;
      waAny.firstAfterSwitchKey = _dedupKey;
      waAny.firstAfterSwitchAudioId = item.id;
      waAny.firstAfterSwitchPid = item.productId || null;
      waAny.firstAfterSwitchAwaitingComplete = true;
      waAny.justSwitchedAtBoundary = false;
    }
    const versionTracker: Map<string, number> =
      waAny.idVersionMap ?? (waAny.idVersionMap = new Map<string, number>());
    const effectiveVer = Number.isFinite(_ver) ? _ver : 0;
    const prevVer = versionTracker.get(item.id);
    const clearFirstPending = () => {
      if (
        waAny.firstAfterSwitchPending &&
        waAny.firstAfterSwitchKey === _dedupKey
      ) {
        waAny.firstAfterSwitchPending = false;
      }
    };
    const syncBufferedItem = () => {
      try {
        const refresh = (buf?: WebAudioItem[]) => {
          if (!Array.isArray(buf)) return;
          for (let i = 0; i < buf.length; i++) {
            const ref = buf[i];
            if (ref && ref.id === item.id) {
              buf[i] = {
                ...ref,
                text: item.text || ref.text || "",
                audioUrl: item.audioUrl
              };
            }
          }
        };
        refresh(wa.pendingNormalFeed);
        refresh(wa.pendingTargetFeed);
      } catch {}
    };
    if (prevVer !== undefined) {
      if (effectiveVer > prevVer) {
        versionTracker.set(item.id, effectiveVer);
        clearFirstPending();
        try {
          // 使用复合键删除旧版本
          const oldPid = item.productId || "NA";
          const oldKey = `${oldPid}::${item.id}::${prevVer}`;
          waAny.addedIds && waAny.addedIds.delete(oldKey);
        } catch {}
        try {
          waAny.addedIds && waAny.addedIds.add(_dedupKey);
        } catch {}
        try {
          const playerAny: any = wa.player;
          const maybe = playerAny?.replaceFutureItemById?.(
            item.id,
            item.text,
            item.audioUrl
          );
          if (maybe && typeof maybe.then === "function") {
            maybe.catch((err: any) => {
              try {
                logWarn(
                  `feed(version-update-error) room=${roomId} id=${item.id}`,
                  err
                );
              } catch {}
            });
          }
        } catch (e) {
          logWarn(`feed(version-update-error) room=${roomId} id=${item.id}`, e);
        }
        syncBufferedItem();
        logInfo(
          `feed(version-update) room=${roomId} id=${item.id} ${prevVer}->${effectiveVer}`
        );
        return;
      }
      if (effectiveVer === prevVer) {
        clearFirstPending();
        logInfo(
          `feed(version-skip-same) room=${roomId} id=${item.id} ver=${effectiveVer}`
        );
        return;
      }
      if (effectiveVer < prevVer) {
        clearFirstPending();
        logInfo(
          `feed(version-skip-older) room=${roomId} id=${item.id} new=${effectiveVer} prev=${prevVer}`
        );
        return;
      }
    }
    versionTracker.set(item.id, effectiveVer);
    const bypassFirstDedup = !!(
      waAny &&
      waAny.firstAfterSwitchPending &&
      waAny.firstAfterSwitchKey &&
      waAny.firstAfterSwitchKey === _dedupKey
    );
    const skipFirstRepeat = !!(
      waAny &&
      waAny.firstAfterSwitchAwaitingComplete &&
      !waAny.firstAfterSwitchPending &&
      waAny.firstAfterSwitchKey &&
      waAny.firstAfterSwitchKey === _dedupKey
    );
    if (skipFirstRepeat) {
      logInfo(`feed(skip-first-repeat) room=${roomId} id=${item.id}`);
      return;
    }
    let addedNow = false;
    const markAdded = () => {
      addedNow = true;
    };
    const dup =
      !bypassFirstDedup && !!(waAny?.addedIds && waAny.addedIds.has(_dedupKey));
    if (dup) {
      logInfo(`feed(dedup-skip) room=${roomId} id=${item.id} key=${_dedupKey}`);
    } else {
      // 准备切换阶段的解码竞争控制：优先只解码目标产品的首条，其他目标产品条目暂存，待首条就绪后再投喂
      const st = this.roomStates.get(roomId);
      const waState = getRoomWAState(this, roomId) as any;
      const targetPid =
        (waState && waState.preparingTargetPid) ||
        st?.pendingProductSwitch?.productId ||
        null;
      const isTarget = !!(
        targetPid &&
        item.productId &&
        item.productId === targetPid
      );
      if (!isUrgent && isTarget && (wa as any)) {
        try {
          if (!(wa as any).pendingTargetFeed)
            (wa as any).pendingTargetFeed = [];
          if ((wa as any).switchTargetReady || (wa as any).targetPrepared) {
            // 首条已就绪：直接正常投喂
          } else if ((wa as any).targetDecoding) {
            // 首条正在解码：暂存后续目标条目，避免并发压制
            (wa as any).pendingTargetFeed.push(item);
            logInfo(`target pending feed buffered id=${item.id}`);
            return;
          } else {
            // 尚未开始：标记正在解码
            (wa as any).targetDecoding = true;
          }
        } catch {}
      }

      if (isUrgent) {
        const p: any = wa.player as any;
        // 读取配置，决定紧急插播策略：front(LIFO) 优先；兼容 fifo
        let useFront = true;
        try {
          const cfg: any =
            (typeof getConfig === "function" ? getConfig() : {}) || ({} as any);
          const mode = String(
            cfg?.Audio?.UrgentInsertMode || "front"
          ).toLowerCase();
          useFront = mode !== "fifo";
        } catch {}
        if (useFront && typeof p.addUrgentNext === "function") {
          // 前插（LIFO）：为避免已预排的普通下一条先行，临时挂起自动衔接，待紧急解码完成后恢复
          try {
            typeof p.setSuppressAutoNext === "function" &&
              p.setSuppressAutoNext(true);
          } catch {}
          try {
            const ret = p.addUrgentNext(item);
            markAdded();
            if (ret && typeof ret.then === "function") {
              ret.finally?.(() => {
                try {
                  typeof p.setSuppressAutoNext === "function" &&
                    p.setSuppressAutoNext(false);
                } catch {}
              });
            } else {
              try {
                typeof p.setSuppressAutoNext === "function" &&
                  p.setSuppressAutoNext(false);
              } catch {}
            }
          } catch {
            try {
              typeof p.setSuppressAutoNext === "function" &&
                p.setSuppressAutoNext(false);
            } catch {}
          }
        } else if (typeof p.addUrgentFIFO === "function") {
          p.addUrgentFIFO(item); // 兼容：插到紧急段末尾（FIFO）
          markAdded();
        } else {
          wa.player.addToPlaylist(item);
          markAdded();
        }
      } else {
        // 非紧急：窗口化 + 目标首条插队（高优先级）
        try {
          const p: any = wa.player as any;
          const status = p.getStatus && p.getStatus();
          const curIdx = Math.max(0, Number(status?.currentIndex || 0));
          const total = Math.max(0, Number(status?.totalItems || 0));
          const lookahead = Math.max(0, total - (curIdx + 1));
          const NORMAL_WINDOW = 3;
          if (!wa.pendingNormalFeed) wa.pendingNormalFeed = [];

          // 🔧 修复：切换等待目标期间，确保不抑制自动衔接旧产品，避免“静音等待”
          try {
            const st2 = this.roomStates.get(roomId);
            const pending = !!(st2 && st2.pendingProductSwitch);
            if (pending && typeof p.setSuppressAutoNext === "function") {
              p.setSuppressAutoNext(false);
            }
          } catch {}

          if (
            isTarget &&
            (wa as any).targetDecoding === true &&
            !(wa as any).targetPrepared &&
            !(wa as any).switchTargetReady
          ) {
            // 目标首条：高优先级解码，尽快就绪
            if (typeof p.addToPlaylistWithPriority === "function") {
              p.addToPlaylistWithPriority(
                item,
                100,
                `TARGET_${item.productId || "NA"}`
              );
            } else {
              wa.player.addToPlaylist(item);
            }
            markAdded();
          } else if (!isTarget && lookahead >= NORMAL_WINDOW) {
            // 当前产品窗口已满：暂存
            wa.pendingNormalFeed.push(item);
            try {
              const txt = item.text ?? "";
              logInfo(
                `[normal-buffer] size=${wa.pendingNormalFeed.length} room=${roomId} id=${item.id} text=${JSON.stringify(txt)}`
              );
            } catch {}
          } else {
            wa.player.addToPlaylist(item);
            markAdded();
          }
        } catch {
          wa.player.addToPlaylist(item);
          markAdded();
        }
      }
      if (
        waAny.firstAfterSwitchPending &&
        waAny.firstAfterSwitchKey === _dedupKey
      ) {
        waAny.firstAfterSwitchPending = false;
      }
      if (addedNow && !bypassFirstDedup) {
        try {
          waAny.addedIds && waAny.addedIds.add(_dedupKey);
        } catch {}
      }
    }
    // 切换挂起时：若该条属于目标产品，等其解码完成后标记“目标已就绪”；如已到边界则立即执行切换
    try {
      const st = this.roomStates.get(roomId);
      const waState2 = getRoomWAState(this, roomId) as any;
      const targetPid =
        (waState2 && waState2.preparingTargetPid) ||
        st?.pendingProductSwitch?.productId ||
        null;
      if (targetPid && item.productId && item.productId === targetPid) {
        const p: any = wa.player as any;
        p.waitUntilDecoded?.(item.id, 10000, 60)
          .then((ok: boolean) => {
            if (ok) {
              (wa as any).switchTargetReady = true;
              (wa as any).targetPrepared = true;
              logInfo("switchTargetReady=true (first target decoded)");
              // 首条解码完成：冲刷暂存的目标条目，恢复正常投喂
              try {
                const buf = (wa as any).pendingTargetFeed || [];
                if (buf.length > 0) {
                  logInfo(`flush pending target feed count=${buf.length}`);
                  for (const it of buf) {
                    try {
                      wa.player.addToPlaylist(it);
                    } catch {}
                  }
                  (wa as any).pendingTargetFeed = [];
                }
              } catch {}
              try {
                (wa as any).targetDecoding = false;
              } catch {}
              // 在“准备切换”模式下不立即切换，等待自然边界（oncomplete）再切
            }
          })
          .catch(() => {});
      }
    } catch {}
    // 若处于挂起切换且该条是目标新产品，立即允许恢复自动推进（解除阻断）
    try {
      const st = this.roomStates.get(roomId);
      if (
        st &&
        st.pendingProductSwitch &&
        item.productId &&
        item.productId === st.pendingProductSwitch.productId
      ) {
        if (typeof (wa.player as any).setSuppressAutoNext === "function") {
          (wa.player as any).setSuppressAutoNext(false);
          logInfo("reset suppressAutoNext=false on target feed");
        }
        try {
          (wa.player as any).setForeHandoff &&
            (wa.player as any).setForeHandoff(false);
          logInfo("reset foreHandoff=false on target feed");
        } catch {}
      }
    } catch {}
    const state = this.roomStates.get(roomId);
    // 若当前未在播，评估是否可以启动：隐藏态必须等 Howler 真正结束（守门，避免双声/重复）
    try {
      const s = wa.player.getStatus();
      const waAudible = !!(
        s &&
        s.isPlaying &&
        !s.muted &&
        s.duration > 0 &&
        s.currentTime < s.duration - 0.01
      );
      if (!waAudible) {
        // 守门：如仍在等待 Howler 边界且 Howler 未到尾声，先不启动 WA
        let allowStart = true;
        try {
          const state2 = this.roomStates.get(roomId);
          const hp: any = state2 && (state2 as any).howlerPlayer;
          const awaiting = !!(getRoomWAState(this, roomId) as any)
            ?.awaitingHowlerBoundary;
          if (hp && typeof hp.getStatus === "function") {
            const hs = hp.getStatus();
            const nearEnd =
              hs &&
              hs.duration > 0 &&
              hs.currentTime >= Math.max(0, hs.duration - 0.25);
            const actuallyPlaying = !!(hs && hs.isPlaying);
            logInfo(
              `handoff(diag): feed gate check room=${roomId} awaiting=${awaiting} hpPlaying=${actuallyPlaying} nearEnd=${nearEnd} hs=${hs?.currentTime?.toFixed?.(2)}/${hs?.duration?.toFixed?.(2)}`
            );
            if (awaiting && actuallyPlaying && !nearEnd) {
              allowStart = false;
              logInfo(
                `gated WA start(feed): awaiting howler boundary room=${roomId}`
              );
            }
          }
        } catch {}
        if (allowStart) {
          // 🔧 修复：只有当前房间才允许有声播放，其他房间强制静音
          const isCurrentRoom = this.currentRoomId === roomId;
          if (isCurrentRoom) {
            try {
              wa.player.setMuted && wa.player.setMuted(false);
            } catch {}
            logInfo(`🔊 设置当前房间${roomId}WebAudio有声播放`);
          } else {
            try {
              wa.player.setMuted && wa.player.setMuted(true);
            } catch {}
            logInfo(`🔇 强制设置非当前房间${roomId}WebAudio静音播放`);
          }
          const sNow = wa.player.getStatus();
          if (!sNow || !sNow.isPlaying) {
            logInfo(
              `WA play() start room=${roomId} (feed, gated-ok) isCurrentRoom=${isCurrentRoom}`
            );
            try {
              wa.player.play();
            } catch {}
            try {
              const stMark = getRoomWAState(this, roomId) as any;
              if (stMark) stMark.awaitingHowlerBoundary = false;
            } catch {}
          }
        }
      }
      try {
        const s2 = wa.player.getStatus();
      } catch {}
    } catch {}
  } catch (e) {
    // 解码失败时静默回退：销毁 WebAudio 分支，交还 Howler
    logError(`WA addToPlaylist error room=${roomId}`, e);
    try {
      wa.player.stop();
      wa.player.destroy();
    } catch {}
    setRoomWAState(this, roomId, null);
  }
}

/** 解码成功后再替换未来项（按 ID 精准更新 WA 列表） */
export async function updateAppleWAItem(
  this: Store,
  roomId: string,
  patch: { id: string; text?: string; audioUrl?: string }
) {
  if (!roomId || !this.roomStates.has(roomId)) return false;
  const wa = getRoomWAState(this, roomId);
  if (!wa || !wa.player) return false;
  try {
    const ok = await (wa.player as any).replaceFutureItemById?.(
      patch.id,
      patch.text,
      patch.audioUrl
    );
    return !!ok;
  } catch {
    return false;
  }
}

/** 显式销毁（可选） */
export function destroyAppleHiddenWebAudio(this: Store, roomId: string) {
  const wa = getRoomWAState(this, roomId);
  if (!wa) return;
  try {
    // 强制进入拆除模式，避免 onended 再次推进或开启保活
    (wa.player as any).tearingDown = true;
  } catch {}
  try {
    const s = wa.player?.getStatus && wa.player.getStatus();
    logInfo(
      `destroy WA start room=${roomId} status=${s ? JSON.stringify({ isPlaying: s.isPlaying, idx: s.currentIndex, active: (s as any).activeSourcesCount, t: s.currentTime?.toFixed?.(2), d: s.duration?.toFixed?.(2) }) : "n/a"}`
    );
    logInfo(
      `destroy WA handlers room=${roomId} vis=${!!(wa as any).visibilityHandler} boundary=${!!(wa as any).boundaryTimer} deferred=${!!(wa as any).deferredStartTimer} unbindFns=${(wa as any)._unbindFns?.length || 0}`
    );
  } catch {}
  // 清理去重集合
  try {
    (wa as any).addedIds && (wa as any).addedIds.clear();
  } catch {}
  try {
    (wa as any).idVersionMap && (wa as any).idVersionMap.clear();
  } catch {}
  // 解绑 visibility 监听
  try {
    if (wa.visibilityHandler) {
      document.removeEventListener(
        "visibilitychange",
        wa.visibilityHandler as any
      );
    }
  } catch {}
  // 清理定时器
  try {
    if (wa.boundaryTimer) {
      clearInterval(wa.boundaryTimer);
      wa.boundaryTimer = undefined;
    }
  } catch {}
  try {
    if (wa.deferredStartTimer) {
      clearInterval(wa.deferredStartTimer);
      wa.deferredStartTimer = undefined;
    }
  } catch {}
  // 解绑 mitt 事件
  try {
    wa._unbindFns &&
      wa._unbindFns.forEach(fn => {
        try {
          fn();
        } catch {}
      });
    wa._unbindFns = [];
  } catch {}
  // 销毁播放器
  try {
    wa.player.stop();
    wa.player.destroy();
  } catch {}
  // 移除绑定的 <audio> sink
  try {
    const el = wa.sinkEl as HTMLAudioElement | null;
    if (el && el.parentNode) el.parentNode.removeChild(el);
  } catch {}
  // 清理 handoff guard
  try {
    const g: any = globalThis as any;
    g.__appleHandoffActiveRooms && g.__appleHandoffActiveRooms.delete(roomId);
  } catch {}
  setRoomWAState(this, roomId, null);
  try {
    logInfo(`destroy WA done room=${roomId}`);
  } catch {}
}

/** 更新所有WebAudio播放器的房间隔离状态（用于房间切换时调用） */
export function updateAppleWebAudioRoomIsolation(this: Store) {
  if (!(isIOSLike() || isSafari())) return; // 非苹果端不处理

  const currentRoomId = this.currentRoomId;
  const roomIds = Array.from(this.roomStates.keys());

  logInfo(
    `更新WebAudio房间隔离: currentRoomId=${currentRoomId}, 总房间数=${roomIds.length}`
  );

  roomIds.forEach(roomId => {
    const wa = getRoomWAState(this, roomId);
    if (!wa || !wa.player) return;

    const isCurrentRoom = roomId === currentRoomId;
    try {
      if (isCurrentRoom) {
        wa.player.setMuted && wa.player.setMuted(false);
        logInfo(`🔊 更新房间${roomId}WebAudio为有声播放`);
      } else {
        wa.player.setMuted && wa.player.setMuted(true);
        logInfo(`🔇 更新房间${roomId}WebAudio为静音播放`);
      }
    } catch (e) {
      logWarn(`更新房间${roomId}WebAudio音量状态失败`, e);
    }
  });
}

/** 将方法动态挂到 store 实例（调用处：store actions/index 组织器 或 直接在使用处 this. 调用） */
export function augmentStoreWithAppleHiddenWebAudio(store: Store) {
  // 幂等挂载：已存在则跳过该方法，但补齐缺失的方法
  if (!(store as any).ensureAppleHiddenWebAudio) {
    (store as any).ensureAppleHiddenWebAudio =
      ensureAppleHiddenWebAudio.bind(store);
  }
  if (!(store as any).feedAppleHiddenWebAudio) {
    (store as any).feedAppleHiddenWebAudio =
      feedAppleHiddenWebAudio.bind(store);
  }
  if (!(store as any).destroyAppleHiddenWebAudio) {
    (store as any).destroyAppleHiddenWebAudio =
      destroyAppleHiddenWebAudio.bind(store);
  }
  if (!(store as any).ensureAppleWASink) {
    (store as any).ensureAppleWASink = ensureAppleWASink.bind(store);
  }
  if (!(store as any).updateAppleWAItem) {
    (store as any).updateAppleWAItem = updateAppleWAItem.bind(store);
  }
  if (!(store as any).prefillAppleWAHead) {
    (store as any).prefillAppleWAHead = prefillAppleWAHead.bind(store);
  }
  if (!(store as any).updateAppleWebAudioRoomIsolation) {
    (store as any).updateAppleWebAudioRoomIsolation =
      updateAppleWebAudioRoomIsolation.bind(store);
  }
}

/** 前台/后台通用：从当前句之后开始，预填 count 条到 WA（仅解码不调度），去重安全 */
export function prefillAppleWAHead(
  this: Store,
  roomId: string,
  count: number = 1
) {
  const st = getRoomWAState(this, roomId);
  if (!st || !st.player) return;
  const state = this.roomStates.get(roomId);
  if (!state || !Array.isArray(state.queue)) return;
  const start = Math.min(
    state.queue.length,
    Math.max(0, state.currentIndex + 1)
  );
  const end = Math.min(
    state.queue.length,
    start + Math.max(0, Math.floor(count))
  );
  if (end <= start) return;
  logInfo(
    `prefill(head) room=${roomId} range=[${start}, ${end}) visible=${!(typeof document !== "undefined" && document.hidden)}`
  );
  for (let i = start; i < end; i++) {
    const it: any = state.queue[i];
    if (!it?.audioUrl || !it?.id) continue;
    const pid3 = (it as any)?.productId || "NA";
    const dedupKey = `${pid3}::${it.id}::${(it as any)?.version ?? state.switchVersion ?? 0}`;
    const already = !!(
      (st as any)?.addedIds && (st as any).addedIds.has(dedupKey)
    );
    if (already) {
      logInfo(`prefill(head-dedup-skip) room=${roomId} id=${it.id} idx=${i}`);
      continue;
    }
    const waItem: WebAudioItem = {
      id: it.id,
      text: it.text || "",
      audioUrl: it.audioUrl
    };
    try {
      st.player.addToPlaylist(waItem);
      try {
        (st as any).addedIds && (st as any).addedIds.add(dedupKey);
      } catch {}
    } catch (e) {
      logError(`prefill(head) addToPlaylist error idx=${i} room=${roomId}`, e);
    }
  }
}
